import hsCodesData from "../data/hs_codes_for_app.json";

export interface HSCodeMapping {
  code: string;
  level: number;
  displayName: string;
}

// Load and process HS codes from JSON file
export const HS_CODE_MAPPINGS: HSCodeMapping[] = hsCodesData;

// Helper function to get HS code by code
export const getHSCodeByCode = (code: string): HSCodeMapping | undefined => {
  return HS_CODE_MAPPINGS.find((mapping) => mapping.code === code);
};

// Helper function to get display name for a code
export const getHSCodeDisplayName = (code: string): string => {
  const mapping = getHSCodeByCode(code);
  return mapping ? mapping.displayName : code;
};

// Helper function to search HS codes
export const searchHSCodes = (
  query: string,
  limit: number = 200
): HSCodeMapping[] => {
  if (!query.trim()) {
    return HS_CODE_MAPPINGS.slice(0, 100); // Show first 100 by default
  }

  const searchTerm = query.toLowerCase();
  const searchCode = query.trim();

  // Filter matching options
  const matches = HS_CODE_MAPPINGS.filter(
    (option) =>
      option.displayName.toLowerCase().includes(searchTerm) ||
      option.code.includes(searchCode)
  );

  // Smart ranking: prioritize exact starts, then code starts, then contains
  const ranked = matches.sort((a, b) => {
    const aCodeStartsWith = a.code.startsWith(searchCode);
    const bCodeStartsWith = b.code.startsWith(searchCode);
    const aDisplayStartsWith = a.displayName
      .toLowerCase()
      .startsWith(searchTerm);
    const bDisplayStartsWith = b.displayName
      .toLowerCase()
      .startsWith(searchTerm);

    // Priority 1: Code starts with query (highest priority)
    if (aCodeStartsWith && !bCodeStartsWith) return -1;
    if (!aCodeStartsWith && bCodeStartsWith) return 1;

    // Priority 2: Display name starts with query
    if (aDisplayStartsWith && !bDisplayStartsWith) return -1;
    if (!aDisplayStartsWith && bDisplayStartsWith) return 1;

    // Priority 3: Shorter codes first (more specific)
    if (aCodeStartsWith && bCodeStartsWith) {
      return a.code.length - b.code.length;
    }

    // Priority 4: Alphabetical by code
    return a.code.localeCompare(b.code);
  });

  return ranked.slice(0, limit);
};
